import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Button, 
  Grid, 
  Typography, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select,
  Box,
  Paper,
  FormHelperText,
  CircularProgress,
  OutlinedInput,
  Chip,
  Divider,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { apiService } from '../../utils/api';
import { sendVerificationEmail } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// 業種リスト
const industries = [
  'スタンダード',
  '激安',
  '高級',
  '待ち合わせ型',
  '学園系',
  'ギャル系',
  '人妻・熟女系',
  '未経験・素人系',
  'ぽっちゃり系',
  '即プレイ系',
  '手コキ専門',
  'SM',
  '風エス',
  'リフレ',
  '女性用風俗'
];

const CorporateRegistrationForm = ({ userId }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(true);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // 入力フォーム状態
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    shop_name: '',
    business_start_time: '',
    business_end_time: '',
    phone_number: '',
    official_line: '',
    job_posting_url: '',
    prefecture_code: '',
    city_codes: [],
    industry: []
  });
  
  // 都道府県と市区町村のデータ
  const [prefectures, setPrefectures] = useState([]);
  const [cities, setCities] = useState([]);
  
  // 初期データ読み込み
  useEffect(() => {
    const fetchPrefectures = async () => {
      try {
        const response = await apiService.get('/api/areas/prefectures');
        setPrefectures(response.data);
      } catch (error) {
        setError('都道府県データの取得に失敗しました');
      }
    };
    
    // ユーザーの既存データがあれば取得
    const fetchUserData = async () => {
      if (userId) {
        try {
          const response = await apiService.get(`/api/users/profile/${userId}`);
          const userData = response.data;
          
          if (userData.corporate_profile) {
            const profile = userData.corporate_profile;
            
            // 時間データをそのまま文字列として使用
            setFormData({
              ...formData,
              shop_name: profile.shop_name || '',
              business_start_time: profile.business_start_time || '',
              business_end_time: profile.business_end_time || '',
              phone_number: profile.phone_number || '',
              official_line: profile.official_line || '',
              job_posting_url: profile.job_posting_url || '',
              prefecture_code: profile.prefecture?.code || '',
              city_codes: profile.cities?.map(city => city.code) || [],
              industry: profile.industry ? (Array.isArray(profile.industry) ? profile.industry : [profile.industry]) : []
            });
            
            // 都道府県が選択されている場合は市区町村を読み込む
            if (profile.prefecture?.code) {
              fetchCities(profile.prefecture.code);
            }
          }
        } catch (error) {
          setError('ユーザーデータの取得に失敗しました');
        }
      }
    };
    
    fetchPrefectures();
    fetchUserData();
  }, [userId]);
  
  // 都道府県が変更されたら市区町村を取得
  const fetchCities = async (prefectureCode) => {
    if (!prefectureCode) {
      setCities([]);
      return;
    }
    
    try {
      const response = await apiService.get(`/api/areas/cities/${prefectureCode}`);
      setCities(response.data);
    } catch (error) {
      setError('市区町村データの取得に失敗しました');
    }
  };

  // ユーザー名の可用性をチェック
  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < 3) return;
    
    setIsCheckingUsername(true);
    try {
      const response = await apiService.get(`/api/auth/check-username?username=${encodeURIComponent(username)}`);
      setUsernameAvailable(response.data.available);
    } catch (error) {
      setUsernameAvailable(false);
    } finally {
      setIsCheckingUsername(false);
    }
  };
  
  // 入力値の変更を処理
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // 都道府県が変更された場合の特別処理
    if (name === 'prefecture_code') {
      // 都道府県変更時に市区町村をクリアして、新しい市区町村リストを取得
      setFormData(prev => ({
        ...prev,
        [name]: value,
        city_codes: [] // 市区町村をリセット
      }));
      
      // 新しい市区町村リストを取得
      fetchCities(value);
    } else if (name === 'city_codes') {
      // 市区町村の複数選択の場合は別途処理
      setFormData(prev => ({
        ...prev,
        city_codes: value
      }));
    } else {
      // 通常の入力値変更処理
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // ユーザー名変更時に可用性チェック
    if (name === 'username' && value.length >= 3) {
      checkUsernameAvailability(value);
    }
  };
  
  // 業種選択の処理
  const handleIndustryChange = (event) => {
    const {
      target: { value },
    } = event;
    
    // 文字列か配列で取得できるのでArray.isArray()でチェック
    setFormData({
      ...formData,
      industry: typeof value === 'string' ? value.split(',') : value,
    });
  };
  
  // メールアドレスの検証
  const validateEmail = () => {
    return /\S+@\S+\.\S+/.test(formData.email);
  };
  
  // パスワードの検証
  const validatePassword = () => {
    return formData.password.length >= 8;
  };
  
  // ユーザー名の検証
  const validateUsername = () => {
    return formData.username.length >= 3 && usernameAvailable;
  };
  
  // フォーム送信処理
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // バリデーション
    if (!formData.shop_name || !formData.prefecture_code || formData.city_codes.length === 0) {
      setError('必須項目を入力してください');
      return;
    }
    
    if (!userId && (!validateEmail() || !validatePassword() || !validateUsername())) {
      setError('入力内容に誤りがあります');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const payload = {
        shop_name: formData.shop_name,
        business_start_time: formData.business_start_time,
        business_end_time: formData.business_end_time,
        phone_number: formData.phone_number,
        official_line: formData.official_line,
        job_posting_url: formData.job_posting_url,
        prefecture_code: formData.prefecture_code,
        city_codes: formData.city_codes,
        industry: formData.industry
      };
      
      let response;
      
      if (userId) {
        // 既存ユーザーの情報更新
        response = await apiService.put(`/api/users/corporate/${userId}`, payload);
      } else {
        // 新規登録
        const registerPayload = {
          ...payload,
          username: formData.username,
          email: formData.email,
          password: formData.password,
          role: 'corporate'
        };
        
        response = await apiService.post('/api/auth/register/corporate', registerPayload);
      }
      
      if (response.status === 200 || response.status === 201) {
        setSuccess(true);
        
        // 新規登録の場合
        if (!userId) {
          // 確認メールを送信（Firebase が独自ドメイン設定に従って送信）。失敗しても登録は継続。
          if (formData.email && formData.password) {
            try {
              await sendVerificationEmail(formData.email, formData.password);
            } catch (mailErr) {
              console.warn('確認メール送信に失敗しました（登録は継続）:', mailErr?.code || mailErr?.message);
            }
          }
          // 登録成功メッセージを表示し、ログインページへリダイレクト
          setTimeout(() => {
            window.location.href = '/login?registered=true';
          }, 3000);
        }
      } else {
        throw new Error('レスポンスエラー');
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        setError(error.response.data.message);
      } else {
        setError('企業情報の登録に失敗しました。後でもう一度お試しください。');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // パスワード表示切り替え
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  
  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {userId 
            ? '企業情報が正常に更新されました。' 
            : '企業アカウントが正常に登録されました。ログインページにリダイレクトします。'}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Typography variant="h5" gutterBottom>
        {userId ? '企業情報編集' : '企業アカウント登録'}
      </Typography>
      
      <Divider sx={{ mb: 3 }} />
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* 新規登録の場合のみユーザー名、メール、パスワードフィールドを表示 */}
          {!userId && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  アカウント情報
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="ユーザー名"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  error={formData.username.length > 0 && !validateUsername()}
                  helperText={
                    formData.username.length > 0 && !validateUsername()
                      ? !usernameAvailable
                        ? 'このユーザー名は既に使用されています'
                        : 'ユーザー名は3文字以上で入力してください'
                      : 'ログイン時に使用するIDです'
                  }
                  InputProps={{
                    endAdornment: isCheckingUsername && (
                      <InputAdornment position="end">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="メールアドレス"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={formData.email.length > 0 && !validateEmail()}
                  helperText={
                    formData.email.length > 0 && !validateEmail()
                      ? '有効なメールアドレスを入力してください'
                      : ''
                  }
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="パスワード"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  error={formData.password.length > 0 && !validatePassword()}
                  helperText={
                    formData.password.length > 0 && !validatePassword()
                      ? 'パスワードは8文字以上で入力してください'
                      : ''
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="パスワードの可視性を切り替える"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Divider />
              </Grid>
            </>
          )}
          
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              店舗情報
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="店舗名"
              name="shop_name"
              value={formData.shop_name}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="営業開始時間"
              name="business_start_time"
              type="time"
              value={formData.business_start_time}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="営業終了時間"
              name="business_end_time"
              type="time"
              value={formData.business_end_time}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="電話番号"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="公式LINE ID"
              name="official_line"
              value={formData.official_line}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="求人ページURL"
              name="job_posting_url"
              value={formData.job_posting_url}
              onChange={handleChange}
            />
          </Grid>
          
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              地域と業種
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel id="prefecture-label">都道府県</InputLabel>
              <Select
                labelId="prefecture-label"
                name="prefecture_code"
                value={formData.prefecture_code}
                onChange={handleChange}
                label="都道府県"
              >
                {prefectures.map((prefecture) => (
                  <MenuItem key={prefecture.code} value={prefecture.code}>
                    {prefecture.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel id="city-label">市区町村</InputLabel>
              <Select
                labelId="city-label"
                multiple
                name="city_codes"
                value={formData.city_codes}
                onChange={handleChange}
                input={<OutlinedInput label="市区町村" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const city = cities.find(c => c.code === value);
                      return (
                        <Chip key={value} label={city ? city.name : value} />
                      );
                    })}
                  </Box>
                )}
                MenuProps={MenuProps}
                disabled={!formData.prefecture_code || cities.length === 0}
              >
                {cities.map((city) => (
                  <MenuItem key={city.code} value={city.code}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
              {!formData.prefecture_code && (
                <FormHelperText>先に都道府県を選択してください</FormHelperText>
              )}
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="industry-label">業種</InputLabel>
              <Select
                labelId="industry-label"
                multiple
                name="industry"
                value={formData.industry}
                onChange={handleIndustryChange}
                input={<OutlinedInput label="業種" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {industries.map((industry) => (
                  <MenuItem key={industry} value={industry}>
                    {industry}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={loading}
              sx={{ minWidth: 200 }}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : userId ? (
                '更新する'
              ) : (
                '登録する'
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default CorporateRegistrationForm; 